import { stableSerialize } from './stableSerialize';

export const queryKeys = {
  recruiter: {
    teamInfo: ['recruiter', 'teamInfo'] as const,
    teamMembers: ['recruiter', 'teamMembers'] as const,
    companyRecruiters: (page: number, pageSize: number) =>
      ['recruiter', 'companyRecruiters', page, pageSize] as const,
    agreements: ['recruiter', 'agreements'] as const,
    pendingInvitations: () => ['recruiter', 'pendingInvitations'] as const,
    agencyFilterCounts: (
      mode: string,
      marketplaceKey: string | null,
      snapshotKey: string,
    ) => ['agencyFilterCounts', mode, marketplaceKey, snapshotKey] as const,
    directoryFilterCounts: (snapshotKey: string) =>
      ['recruiter', 'directoryFilterCounts', snapshotKey] as const,
    jobAssignmentsFilterCounts: (snapshotKey: string) =>
      ['recruiter', 'jobAssignmentsFilterCounts', snapshotKey] as const,
    jobAssignmentsFilterOptions: () =>
      ['recruiter', 'jobAssignmentsFilterOptions'] as const,
    lists: () => ['recruiterLists'] as const,
    agencyFilterCountsPrefix: ['agencyFilterCounts'] as const,
    agencyListMembersPrefix: ['agencyListMembers'] as const,
    agencyListAggregatesPrefix: ['agencyListAggregates'] as const,
    jobListAssignmentsPrefix: ['jobListAssignments'] as const,
    agencyListMembers: (mode: string, fetchKey: string) =>
      ['agencyListMembers', mode, fetchKey] as const,
    agencyListAggregates: (
      mode: string,
      marketplaceKey: string,
      aggregatesKey: string,
    ) => ['agencyListAggregates', mode, marketplaceKey, aggregatesKey] as const,
    marketplaceJob: (jobId: string | null) =>
      ['marketplaceJob', jobId] as const,
    jobListAssignments: (jobId: string | null) =>
      ['jobListAssignments', jobId] as const,
    detail: (recruiterId: string) =>
      ['recruiter', 'detail', recruiterId] as const,
    companyAgreement: (agreementId: string) =>
      ['recruiter', 'companyAgreement', agreementId] as const,
    companyAgreementByCompany: (companyId: string) =>
      ['recruiter', 'companyAgreementByCompany', companyId] as const,
    previouslySelected: (query: string) =>
      ['recruiter', 'previouslySelected', query] as const,
    inProcessCandidates: (recruiterId: string) =>
      ['recruiter', 'inProcessCandidates', recruiterId] as const,
    companyList: () => ['recruiter', 'companyList'] as const,
    agencyCompanies: (paramsKey = '') =>
      ['agencyCompanies', paramsKey] as const,
    agencyCompaniesPrefix: ['agencyCompanies'] as const,
    agencyCompanyMembersPrefix: ['agencyCompanyMembers'] as const,
    agencyCompanyAssignmentsByJob: (jobId: string | null) =>
      ['agencyCompanyAssignmentsByJob', jobId] as const,
    feeTemplateAgencyCompanies: (feeTemplateId: string) =>
      ['feeTemplateAgencyCompanies', feeTemplateId] as const,
    workspaces: () => ['recruiter', 'workspaces'] as const,
    projects: () => ['recruiter', 'projects'] as const,
  },
  company: {
    settings: (companyId?: string) =>
      ['company', 'settings', companyId ?? 'current'] as const,
    defaultPermissions: () => ['company', 'defaultPermissions'] as const,
    hiringTeamRoles: () => ['company', 'hiringTeamRoles'] as const,
    users: () => ['company', 'users'] as const,
    byName: (name: string) => ['company', 'byName', name] as const,
    byId: (companyId: string) => ['company', 'byId', companyId] as const,
    feeTemplates: () => ['company', 'feeTemplates'] as const,
    feeTemplate: (feeTemplateId: string) =>
      ['company', 'feeTemplate', feeTemplateId] as const,
    activityFeed: (companyId: string) =>
      ['company', 'activityFeed', companyId] as const,
    inviteEmailTemplate: (language?: string) =>
      ['company', 'inviteEmailTemplate', language ?? ''] as const,
    inviteEmailPreview: (payload: unknown) =>
      ['company', 'inviteEmailPreview', stableSerialize(payload)] as const,
    departments: (companyId: string) =>
      ['company', 'departments', companyId] as const,
    defaultScreeningQuestions: () =>
      ['company', 'defaultScreeningQuestions'] as const,
    defaultJobExtensionFields: () =>
      ['company', 'defaultJobExtensionFields'] as const,
  },
  remoteOrg: {
    info: () => ['remoteOrg', 'info'] as const,
    /** Prefix — invalidates all org-specific settings queries */
    settingsPrefix: ['remoteOrg', 'settings'] as const,
    settings: (atsId: string, orgId: string) =>
      ['remoteOrg', 'settings', atsId, orgId] as const,
    roles: (atsId: string, orgId: string) =>
      ['remoteOrg', 'roles', atsId, orgId] as const,
    /** Prefix — invalidates all org-specific provider-settings queries */
    providerSettingsPrefix: ['remoteOrg', 'providerSettings'] as const,
    providerSettings: (atsId: string, orgId: string) =>
      ['remoteOrg', 'providerSettings', atsId, orgId] as const,
    atsUsers: (atsId: string, orgId: string) =>
      ['remoteOrg', 'atsUsers', atsId, orgId] as const,
    recruiteeGdpr: (atsId: string, orgId: string) =>
      ['remoteOrg', 'recruiteeGdpr', atsId, orgId] as const,
  },
  masterContracts: {
    templates: ['masterContracts', 'templates'] as const,
  },
  reference: {
    all: ['reference'] as const,
    skills: () => [...queryKeys.reference.all, 'skills'] as const,
    cities: () => [...queryKeys.reference.all, 'cities'] as const,
    seniority: () => [...queryKeys.reference.all, 'seniority'] as const,
    certificates: () => [...queryKeys.reference.all, 'certificates'] as const,
    education: () => [...queryKeys.reference.all, 'education'] as const,
    roles: () => [...queryKeys.reference.all, 'roles'] as const,
    bundle: (extra?: { candidateId?: string; includeRoles?: boolean }) =>
      [...queryKeys.reference.all, 'bundle', extra ?? {}] as const,
    location: () => [...queryKeys.reference.all, 'location'] as const,
    languages: () => [...queryKeys.reference.all, 'languages'] as const,
    technicalTools: () =>
      [...queryKeys.reference.all, 'technicalTools'] as const,
  },
  listings: {
    all: ['listings'] as const,
    page: (id: string, snapshot: unknown, context: unknown) =>
      [
        ...queryKeys.listings.all,
        id,
        stableSerialize(snapshot),
        stableSerialize(context),
      ] as const,
    facetBaseline: (id: string, context?: unknown) =>
      [
        ...queryKeys.listings.all,
        id,
        'facetBaseline',
        stableSerialize(context ?? null),
      ] as const,
  },
  candidates: {
    all: ['candidates'] as const,
    detail: (id: string) =>
      [...queryKeys.candidates.all, 'detail', id] as const,
    history: (id: string) =>
      [...queryKeys.candidates.all, 'history', id] as const,
    byInterview: (interviewId: string) =>
      [...queryKeys.candidates.all, 'byInterview', interviewId] as const,
    kanban: () => [...queryKeys.candidates.all, 'kanban'] as const,
    inProcessList: () =>
      [...queryKeys.candidates.all, 'inProcessList'] as const,
    filterCounts: (role: string, view: string, snapshotKey: string) =>
      [
        ...queryKeys.candidates.all,
        'filterCounts',
        role,
        view,
        snapshotKey,
      ] as const,
  },
  jobs: {
    all: ['jobs'] as const,
    detail: (id: string) => [...queryKeys.jobs.all, 'detail', id] as const,
    subsection: (id: string, section: string) =>
      [...queryKeys.jobs.all, 'detail', id, 'subsection', section] as const,
    filterMeta: (role: string) =>
      [...queryKeys.jobs.all, 'filterMeta', role] as const,
    filterCounts: (role: string, tab: string, snapshotKey: string) =>
      [...queryKeys.jobs.all, 'filterCounts', role, tab, snapshotKey] as const,
    activityFeed: (id: string) =>
      [...queryKeys.jobs.all, 'activityFeed', id] as const,
    effectiveScreeningQuestions: (jobId: string) =>
      [...queryKeys.jobs.all, 'effectiveScreeningQuestions', jobId] as const,
    screeningQuestionsByStage: (jobId: string) =>
      [...queryKeys.jobs.all, 'screeningQuestionsByStage', jobId] as const,
    similarJobs: (jobId: string) =>
      [...queryKeys.jobs.all, 'similar', jobId] as const,
    inviteTokenValidation: (jobId: string, token: string) =>
      [...queryKeys.jobs.all, 'inviteTokenValidation', jobId, token] as const,
    recruitersSelectedForJob: (query: string, jobId: string) =>
      [...queryKeys.jobs.all, 'recruitersSelected', query, jobId] as const,
  },
  interviews: {
    all: ['interviews'] as const,
    detail: (id: string) =>
      [...queryKeys.interviews.all, 'detail', id] as const,
    stagesStatuses: () =>
      [...queryKeys.interviews.all, 'stagesStatuses'] as const,
    statuses: () => [...queryKeys.interviews.all, 'statuses'] as const,
    clientStages: () => [...queryKeys.interviews.all, 'clientStages'] as const,
    stages: () => [...queryKeys.interviews.all, 'stages'] as const,
    jobsStages: () => [...queryKeys.interviews.all, 'jobsStages'] as const,
    room: (id: string) => [...queryKeys.interviews.all, 'room', id] as const,
    feedback: (interviewId: string, feedbackId: string) =>
      [
        ...queryKeys.interviews.all,
        'feedback',
        interviewId,
        feedbackId,
      ] as const,
    proposedDates: (interviewId: string) =>
      [...queryKeys.interviews.all, 'proposedDates', interviewId] as const,
    rejectedFeedback: (interviewId: string) =>
      [...queryKeys.interviews.all, 'rejectedFeedback', interviewId] as const,
  },
  integrations: {
    all: ['integrations'] as const,
    syncStatus: () => [...queryKeys.integrations.all, 'syncStatus'] as const,
    atsStatus: (params: Record<string, string>) =>
      [
        ...queryKeys.integrations.all,
        'atsStatus',
        stableSerialize(params),
      ] as const,
    atsJobStatus: (jobId: string) =>
      [...queryKeys.integrations.all, 'atsJobStatus', jobId] as const,
    defaultStageMappings: () =>
      [...queryKeys.integrations.all, 'defaultStageMappings'] as const,
  },
  employmentTypes: {
    all: ['employmentTypes'] as const,
    list: () => [...queryKeys.employmentTypes.all, 'list'] as const,
  },
  calendar: {
    all: ['calendar'] as const,
    events: (params: unknown) =>
      [...queryKeys.calendar.all, 'events', stableSerialize(params)] as const,
  },
  metrics: {
    client: () => ['metrics', 'client'] as const,
    recruiter: () => ['metrics', 'recruiter'] as const,
    admin: (params?: {
      dateFrom?: string;
      dateTo?: string;
      clientCompanyId?: string;
      employmentTypeId?: string;
    }) => ['metrics', 'admin', params ?? {}] as const,
  },
  dashboard: {
    clientCandidates: (searchSuffix: string) =>
      ['dashboard', 'clientCandidates', searchSuffix] as const,
    recruiterJobsAll: ['dashboard', 'recruiterJobs'] as const,
    recruiterJobs: (filterIndex: number, searchSuffix: string) =>
      [
        ...queryKeys.dashboard.recruiterJobsAll,
        filterIndex,
        searchSuffix,
      ] as const,
  },
  admin: {
    monitoring: () => ['admin', 'monitoring'] as const,
    monitoringOffSyncCompanies: () =>
      [...queryKeys.admin.monitoring(), 'offSyncCompanies'] as const,
    monitoringProvidersReauth: () =>
      [...queryKeys.admin.monitoring(), 'providersReauth'] as const,
    monitoringErrorTypes: () =>
      [...queryKeys.admin.monitoring(), 'errorTypes'] as const,
    offSyncCandidates: (companyId: string) =>
      [
        ...queryKeys.admin.monitoring(),
        'offSyncCandidates',
        companyId,
      ] as const,
    client: (id: string) => ['admin', 'client', id] as const,
    partnerPrograms: () => ['admin', 'partnerPrograms'] as const,
    switchUserClients: (query = '') =>
      ['admin', 'switchUser', 'clients', query] as const,
    switchUserRecruiters: (query = '') =>
      ['admin', 'switchUser', 'recruiters', query] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    client: (endpoint: string, paramsKey: string) =>
      [...queryKeys.analytics.all, 'client', endpoint, paramsKey] as const,
    recruiter: (endpoint: string, paramsKey: string) =>
      [...queryKeys.analytics.all, 'recruiter', endpoint, paramsKey] as const,
    adoption: (paramsKey: string) =>
      [...queryKeys.analytics.all, 'adoption', paramsKey] as const,
  },
  embed: {
    all: ['embed'] as const,
    bootstrap: (params: unknown) =>
      [...queryKeys.embed.all, 'bootstrap', stableSerialize(params)] as const,
    cubeApi: (isDemoMode: boolean) =>
      [...queryKeys.embed.all, 'cubeApi', isDemoMode] as const,
    chatBootstrap: (params: unknown) =>
      [
        ...queryKeys.embed.all,
        'chatBootstrap',
        stableSerialize(params),
      ] as const,
  },
  messages: {
    all: ['messages'] as const,
    unreadGlobal: () =>
      [...queryKeys.messages.all, 'unread', 'global'] as const,
    unreadSupport: () =>
      [...queryKeys.messages.all, 'unread', 'support'] as const,
    unreadInterview: (id: string) =>
      [...queryKeys.messages.all, 'unread', 'interview', id] as const,
    contacts: (role: string) =>
      [...queryKeys.messages.all, 'contacts', role] as const,
    adminContacts: (searchQuery: string) =>
      [...queryKeys.messages.contacts('admin'), searchQuery] as const,
    conversationsFeed: (searchQuery: string) =>
      [...queryKeys.messages.all, 'conversations', searchQuery] as const,
    thread: (type: string, id: string) =>
      [...queryKeys.messages.all, 'thread', type, id] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    unread: () => [...queryKeys.notifications.all, 'unread'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
    settings: () => [...queryKeys.notifications.all, 'settings'] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    detail: (invoiceId: string) =>
      [...queryKeys.invoices.all, 'detail', invoiceId] as const,
  },
  mfa: {
    all: ['mfa'] as const,
    setup: () => [...queryKeys.mfa.all, 'setup'] as const,
    status: () => [...queryKeys.mfa.all, 'status'] as const,
    pendingAppSetup: (mfaToken: string) =>
      [...queryKeys.mfa.all, 'pending-app-setup', mfaToken] as const,
  },
  magicLink: {
    all: ['magicLink'] as const,
    init: (path: string) => [...queryKeys.magicLink.all, 'init', path] as const,
  },
  demo: {
    all: ['demo'] as const,
    preflight: () => [...queryKeys.demo.all, 'preflight'] as const,
  },
  user: {
    tablePreference: (tableId: string, schemaVersion: number, userId: string) =>
      ['user', 'tablePreference', userId, tableId, schemaVersion] as const,
    recruiterInfo: () => ['user', 'recruiterInfo'] as const,
    invitedDetails: (uid: string, token: string, profileId: string | null) =>
      ['user', 'invitedDetails', uid, token, profileId ?? ''] as const,
  },
} as const;

/** Shared invalidation set after recruiter list / agency membership mutations */
export const recruiterAgencyInvalidationKeys = [
  queryKeys.recruiter.lists(),
  queryKeys.recruiter.agencyFilterCountsPrefix,
  queryKeys.recruiter.agencyListMembersPrefix,
  queryKeys.recruiter.agencyListAggregatesPrefix,
  queryKeys.recruiter.agencyCompaniesPrefix,
  queryKeys.recruiter.agencyCompanyMembersPrefix,
  queryKeys.listings.all,
] as const;
